import { SvgIcon } from '@/components/UI/Common/SvgIcon';
import { type MessageImage } from '@/types/chats';
import { getBackUrl } from '@/utils/helpers';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type ChatsImageViewerProps = {
  images: MessageImage[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
};

export const ChatsImageViewer = ({
  images,
  initialIndex,
  isOpen,
  onClose,
}: ChatsImageViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const backUrl = getBackUrl();
  const hasMultiple = images.length > 1;

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex((index) => (index > 0 ? index - 1 : images.length - 1));
  }, [images.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((index) => (index < images.length - 1 ? index + 1 : 0));
  }, [images.length]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, goPrev, goNext]);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр изображений"
    >
      <div className="absolute inset-0 bg-black/90" onClick={onClose} aria-hidden="true" />

      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 text-white hover:opacity-80 cursor-pointer"
        aria-label="Закрыть"
      >
        <SvgIcon name="close" size={28} color="#fff" />
      </button>

      {hasMultiple && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            goPrev();
          }}
          className="absolute left-2 sm:left-4 z-10 text-white hover:opacity-80 cursor-pointer"
          aria-label="Предыдущее изображение"
        >
          <SvgIcon name="arrow-left" size={36} color="#fff" />
        </button>
      )}

      <img
        src={`${backUrl}/uploads/${currentImage.upload.path}`}
        alt=""
        className="relative z-[1] max-h-[90vh] max-w-[90vw] object-contain select-none"
        onClick={(event) => event.stopPropagation()}
      />

      {hasMultiple && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            goNext();
          }}
          className="absolute right-2 sm:right-4 z-10 text-white hover:opacity-80 cursor-pointer"
          aria-label="Следующее изображение"
        >
          <div className="scale-x-[-1]">
            <SvgIcon name="arrow-left" size={36} color="#fff" />
          </div>
        </button>
      )}

      {hasMultiple && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 text-white text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>,
    document.body,
  );
};
